import React, { useState, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';

import GridPagination from './GridPagination';

// Helper function to access nested properties using path
const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;

  const pathArray = path.split('.');
  let current = obj;

  for (const key of pathArray) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }

  return current;
};

const GridRenderer = ({ data = [], config, state, isBusy, onPaginationChange }) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  let hasNextPage = data.length >= state.pagination.pageSize;

  // Transform the config columns to the format expected by TanStack Table
  const columns = useMemo(() => {
    return config.columns.map((column) => {
      // Define column configuration
      return {
        id: column.name,
        accessorFn: (row) => getNestedValue(row, column.name),
        header: column.header, // Store the header text directly
        cell: info => {
          const value = info.getValue();

          // Apply type-specific rendering
          switch (column.type) {
            case 'date':
              return value ? (
                <div>{new Date(value).toLocaleDateString()}</div>
              ) : '';
            case 'bool':
              return (
                <div className={`font-medium ${value ? 'text-green-600' : 'text-red-600'}`}>
                  {value ? 'Yes' : 'No'}
                </div>
              );
            case 'number':
              return (
                <div className="text-right">
                  {typeof value === 'number' ? value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) : value}
                </div>
              );
            case 'email':
              return value ? (
                <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
                  {value}
                </a>
              ) : '';
            case 'link':
              return value ? (
                <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {value}
                </a>
              ) : '';
            case 'string':
            default:
              return <div>{value}</div>;
          }
        },
        enableSorting: column.sortable || false,
      };
    });
  }, [config.columns]);

  // Table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination: state.pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    manualPagination: true,
    pageCount: -1, // We don't know the total page count    
    onPaginationChange: onPaginationChange,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Input
          placeholder="Search..."
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
        {config.actions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Actions</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {config.actions.map((action, index) => (
                <DropdownMenuItem key={index} onClick={action.onClick}>
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {!header.isPlaceholder && (
                        <div className="flex items-center space-x-2">
                          <span>{header.column.columnDef.header}</span>
                          {header.column.getCanSort() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-ml-3 h-8 data-[state=sorted]:bg-accent"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {{
                                asc: <ChevronUp className="h-4 w-4" />,
                                desc: <ChevronDown className="h-4 w-4" />,
                                false: <ChevronsUpDown className="h-4 w-4 opacity-50" />,
                              }[isSorted || 'false']}
                            </Button>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {config.pagination && (
        <GridPagination
          pageIndex={state.pagination.pageIndex}
          pageSize={state.pagination.pageSize}
          hasNextPage={hasNextPage}
          isLoading={isBusy}
          onPaginationChange={onPaginationChange}
        />
      )}
    </div>
  );
};

export { GridRenderer };